import { useMemo } from "react";
import { useQuery, QueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface BadgeRow {
  id: string;
  name: string;
  description: string | null;
  color_key: string;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
}

export interface HeldBadge {
  badge_id: string;
  name: string;
  description: string | null;
  color_key: string;
  awarded_at: string;
}

/** Every query key touched by badge data; invalidate after any badge action. */
export const BADGE_QUERY_KEYS = [["badges"], ["badge_holders"], ["user_badges"], ["admin_badges"]] as const;

export function invalidateBadgeQueries(queryClient: QueryClient) {
  BADGE_QUERY_KEYS.forEach((key) => queryClient.invalidateQueries({ queryKey: [...key] }));
}

export function useBadges() {
  return useQuery({
    queryKey: ["badges"],
    queryFn: async (): Promise<BadgeRow[]> => {
      const { data, error } = await supabase.from("badges").select("*").order("name");
      if (error) throw error;
      return (data || []) as BadgeRow[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useActiveBadges() {
  const query = useBadges();
  const active = useMemo(() => (query.data || []).filter((b) => b.is_active), [query.data]);
  return { ...query, data: active };
}

/**
 * One query for every earned active badge, cached 5 minutes, mapped by user id.
 * Used by UserName so a page of names never triggers per-row lookups.
 */
export function useBadgeHolders() {
  const query = useQuery({
    queryKey: ["badge_holders"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_badges")
        .select("user_id, badge_id, awarded_at, badges!inner(name, description, color_key, is_active)")
        .eq("badges.is_active", true);
      if (error) throw error;
      return data || [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const map = useMemo(() => {
    const m = new Map<string, HeldBadge[]>();
    for (const row of query.data || []) {
      const b = row.badges as unknown as { name: string; description: string | null; color_key: string };
      const list = m.get(row.user_id) || [];
      list.push({
        badge_id: row.badge_id,
        name: b.name,
        description: b.description,
        color_key: b.color_key,
        awarded_at: row.awarded_at,
      });
      m.set(row.user_id, list);
    }
    return m;
  }, [query.data]);

  return { ...query, map };
}

export function useUserBadgesFor(userId: string | undefined | null) {
  const { map, isLoading } = useBadgeHolders();
  const badges = userId ? map.get(userId) || [] : [];
  return { badges, isLoading };
}

export async function setContentBadge(itemType: "course" | "curriculum", itemId: string, badgeId: string | null) {
  const { error } = await supabase.rpc("set_content_badge", {
    _item_type: itemType,
    _item_id: itemId,
    _badge_id: badgeId as string,
  });
  if (error) throw error;
}

/**
 * Mirrors set_content_badge's rules so the picker can be hidden when the server would refuse.
 * The server remains the authority.
 */
export function useCanSetContentBadge(
  itemType: "course" | "curriculum",
  itemId: string | null | undefined,
  userId: string | null | undefined,
  isAdmin: boolean
) {
  return useQuery({
    queryKey: ["badges", "can_set", itemType, itemId, userId, isAdmin],
    queryFn: async (): Promise<boolean> => {
      if (isAdmin) return true;
      if (!userId || !itemId) return false;
      const { data: taught } = await supabase
        .from("course_instructors")
        .select("course_id")
        .eq("instructor_id", userId);
      const coIds = (taught || []).map((t) => t.course_id);
      if (itemType === "course") {
        if (coIds.includes(itemId)) return true;
        const { data } = await supabase.from("courses").select("instructor_id").eq("id", itemId).maybeSingle();
        return data?.instructor_id === userId;
      }
      const { data: links } = await supabase
        .from("curriculum_courses")
        .select("course_id, courses!inner(instructor_id)")
        .eq("curriculum_id", itemId);
      return (links || []).some(
        (l) => coIds.includes(l.course_id) || (l.courses as unknown as { instructor_id: string | null })?.instructor_id === userId
      );
    },
    enabled: isAdmin || (!!userId && !!itemId),
  });
}
